import supabase from "@/services/supabaseClient";
import { createUserNotification } from "@/services/ehrAccessService.js";
const SUPPORT_TYPES = new Set([
  "technical_issue",
  "billing_inquiry",
  "account_access",
  "consultation_support",
  "general_inquiry",
  "feature_request",
]);

const SUPPORT_PRIORITIES = new Set(["low", "medium", "high", "urgent"]);
const SUPPORT_STATUSES = new Set(["open", "in_progress", "resolved", "closed"]);
const SUPPORT_ROLES = new Set(["patient", "doctor", "pharmacy", "practice_team", "external", "unknown"]);
const DOCTOR_SALUTATIONS = new Set(["Dr.", "Prof.", "Rn.", "Mr.", "Mme."]);
const PATIENT_SALUTATIONS = new Set(["Mr.", "Mrs.", "Ms.", "Miss", "Mme."]);

function cleanText(value) {
  return String(value || "").trim();
}

function normalizeSupportType(value) {
  const normalized = cleanText(value).toLowerCase();
  return SUPPORT_TYPES.has(normalized) ? normalized : "general_inquiry";
}

function normalizePriority(value) {
  const normalized = cleanText(value).toLowerCase();
  return SUPPORT_PRIORITIES.has(normalized) ? normalized : "medium";
}

function normalizeStatus(value) {
  const normalized = cleanText(value).toLowerCase();
  return SUPPORT_STATUSES.has(normalized) ? normalized : "";
}

function normalizeSupportRole(value) {
  const normalized = cleanText(value).toLowerCase();
  return SUPPORT_ROLES.has(normalized) ? normalized : "";
}

function normalizeMetadata(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function safeErrorMessage(error, fallback) {
  const message = cleanText(error?.message);
  return message || fallback;
}

function logSupportError(label, error, context = {}) {
  if (typeof console === "undefined") return;
  console.warn(`[supportService] ${label}`, {
    message: error?.message || String(error || ""),
    code: error?.code,
    details: error?.details,
    hint: error?.hint,
    ...context,
  });
}

function maskEmail(email) {
  const value = cleanText(email);
  if (!value || !value.includes("@")) return "";
  const [local, domain] = value.split("@");
  const safeLocal = local.length <= 2 ? `${local.slice(0, 1)}*` : `${local.slice(0, 2)}***`;
  return `${safeLocal}@${domain}`;
}

function looksLikeEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanText(value));
}

function formatNamePart(value) {
  return cleanText(value)
    .split(/([\s-]+)/)
    .map((part) => {
      if (!part || /^[\s-]+$/.test(part)) return part;
      return `${part.charAt(0).toLocaleUpperCase()}${part.slice(1)}`;
    })
    .join("");
}

function normalizeSalutation(role, value) {
  const raw = cleanText(value);
  const allowed = role === "doctor" ? DOCTOR_SALUTATIONS : PATIENT_SALUTATIONS;
  if (allowed.has(raw)) return raw;
  if (role === "doctor" && /^dr\.?$/i.test(raw)) return "Dr.";
  return "";
}

function familyNameFrom(...rows) {
  for (const row of rows) {
    if (!row) continue;
    const explicit = cleanText(row.last_name || row.family_name || row.surname);
    if (explicit) return explicit;

    const fullName = cleanText(row.full_name || row.display_name || row.name);
    const parts = fullName.split(/\s+/).filter(Boolean);
    if (parts.length > 1) return parts[parts.length - 1];
  }
  return "";
}

function supportRequesterDisplayName({ role = "", profile = null, doctorProfile = null, storedName = "", email = "" } = {}) {
  const normalizedRole = normalizeSupportRole(role || profile?.role);
  const stored = looksLikeEmail(storedName) ? "" : cleanText(storedName);
  const maskedEmail = maskEmail(email || profile?.email || doctorProfile?.email);

  if (normalizedRole === "doctor") {
    const familyName = familyNameFrom(doctorProfile, profile, { full_name: stored });
    const salutation = normalizeSalutation("doctor", doctorProfile?.salutation || profile?.salutation) || "Dr.";
    if (familyName) return `${salutation} ${formatNamePart(familyName)}`.trim();
    return stored || maskedEmail || "Doctor requester";
  }

  if (normalizedRole === "pharmacy") {
    const pharmacyName = cleanText(
      profile?.pharmacy_name ||
        profile?.organization_name ||
        profile?.business_name ||
        profile?.display_name ||
        stored,
    );
    return pharmacyName || maskedEmail || "Pharmacy requester";
  }

  const patientFamilyName = familyNameFrom(profile, { full_name: stored });
  const patientSalutation = normalizeSalutation("patient", profile?.salutation);
  if (patientSalutation && patientFamilyName) {
    return `${patientSalutation} ${formatNamePart(patientFamilyName)}`.trim();
  }

  return cleanText(profile?.display_name) || stored || maskedEmail || "Patient requester";
}

async function getCurrentUser() {
  const {
    data: { user },
    error,
  } = await supabase.auth.getUser();
  if (error) throw error;
  return user || null;
}

async function getRequesterProfile(userId) {
  if (!userId) return null;
  const { data, error } = await supabase
    .from("profiles")
    .select("display_name,avatar_url,role,email")
    .eq("user_id", userId)
    .maybeSingle();
  if (error) {
    logSupportError("profile lookup failed", error, { userId });
    return null;
  }
  const profile = data || null;
  if (normalizeSupportRole(profile?.role) !== "doctor") return profile;

  const { data: doctorProfile, error: doctorError } = await supabase
    .from("doctor_profiles")
    .select("full_name,first_name,last_name,salutation,email")
    .eq("user_id", userId)
    .maybeSingle();
  if (doctorError) {
    logSupportError("doctor profile lookup failed", doctorError, { userId });
    return profile;
  }

  return {
    ...profile,
    doctor_full_name: cleanText(doctorProfile?.full_name),
    doctor_first_name: cleanText(doctorProfile?.first_name),
    doctor_last_name: cleanText(doctorProfile?.last_name),
    doctor_salutation: cleanText(doctorProfile?.salutation),
    doctor_email: cleanText(doctorProfile?.email),
  };
}

function requesterName(profile, user) {
  if (normalizeSupportRole(profile?.role) === "doctor") {
    return supportRequesterDisplayName({
      role: "doctor",
      profile,
      doctorProfile: {
        full_name: profile?.doctor_full_name,
        first_name: profile?.doctor_first_name,
        last_name: profile?.doctor_last_name,
        salutation: profile?.doctor_salutation,
        email: profile?.doctor_email,
      },
      email: user?.email,
    });
  }

  return (
    cleanText(profile?.display_name) ||
    cleanText(user?.email).split("@")[0] ||
    "AfriMed™ user"
  );
}

function resolveRequesterLabel(ticket, profile = null, doctorProfile = null) {
  const role = normalizeSupportRole(ticket?.requester_role || profile?.role);
  return supportRequesterDisplayName({
    role,
    profile,
    doctorProfile,
    storedName: ticket?.requester_name,
    email: ticket?.requester_email,
  });
}

function buildTicketInsert(ticketData, user, profile) {
  const supportRole =
    normalizeSupportRole(ticketData?.requesterRole) ||
    normalizeSupportRole(ticketData?.role) ||
    normalizeSupportRole(profile?.role);
  const type = normalizeSupportType(ticketData?.type || ticketData?.category);
  const category = cleanText(ticketData?.category) || type;
  const source = cleanText(ticketData?.source) || "help-support";
  const sourceRoute = cleanText(ticketData?.sourceRoute) || "/help-support";
  const metadata = normalizeMetadata(ticketData?.metadata);
  const tags = Array.from(
    new Set(
      [
        ...(Array.isArray(ticketData?.tags) ? ticketData.tags : []),
        supportRole ? `role:${supportRole}` : "",
        category ? `category:${category}` : "",
        source ? `source:${source}` : "",
      ]
        .map(cleanText)
        .filter(Boolean),
    ),
  );

  return {
    subject: cleanText(ticketData?.subject),
    description: cleanText(ticketData?.description),
    type,
    category,
    priority: normalizePriority(ticketData?.priority),
    requester_id: user.id,
    requester_email: cleanText(user.email),
    requester_name: requesterName(profile, user),
    requester_role: supportRole || null,
    source,
    source_route: sourceRoute,
    related_appointment_id: cleanText(ticketData?.relatedAppointmentId) || null,
    metadata_json: {
      ...metadata,
      requester_role: supportRole || null,
      category,
      source,
      source_route: sourceRoute,
    },
    tags,
  };
}

function normalizeTicket(row) {
  if (!row || typeof row !== "object") return row;
  return {
    ...row,
    category: row.category || row.type || "",
    requester_role: row.requester_role || "",
    message_count: Array.isArray(row.message_count)
      ? Number(row.message_count?.[0]?.count || 0)
      : typeof row.message_count === "object"
        ? Number(row.message_count?.count || 0)
        : Number(row.message_count || 0),
  };
}

async function enrichRequesterIdentity(tickets) {
  const rows = Array.isArray(tickets) ? tickets : tickets ? [tickets] : [];
  if (!rows.length) return tickets;

  const requesterIds = Array.from(
    new Set(rows.map((row) => cleanText(row?.requester_id)).filter(Boolean)),
  );
  if (!requesterIds.length) return tickets;

  const [profilesResult, doctorsResult] = await Promise.all([
    supabase
      .from("profiles")
      .select("user_id,display_name,role,email")
      .in("user_id", requesterIds),
    supabase
      .from("doctor_profiles")
      .select("user_id,full_name,first_name,last_name,salutation,email")
      .in("user_id", requesterIds),
  ]);

  if (profilesResult.error) {
    logSupportError("requester profile enrichment failed", profilesResult.error);
  }
  if (doctorsResult.error) {
    logSupportError("doctor requester enrichment failed", doctorsResult.error);
  }

  const profilesByUserId = new Map(
    (profilesResult.data || []).map((profile) => [cleanText(profile?.user_id), profile]),
  );
  const doctorsByUserId = new Map(
    (doctorsResult.data || []).map((profile) => [cleanText(profile?.user_id), profile]),
  );

  const enriched = rows.map((row) => {
    const userId = cleanText(row?.requester_id);
    const profile = profilesByUserId.get(userId) || null;
    const doctorProfile = doctorsByUserId.get(userId) || null;
    return {
      ...row,
      requester_display_name: resolveRequesterLabel(row, profile, doctorProfile),
      requester_email_masked: maskEmail(row?.requester_email || profile?.email || doctorProfile?.email),
    };
  });

  return Array.isArray(tickets) ? enriched : enriched[0] || tickets;
}

async function notifySupportRequester(ticketId, notification = {}) {
  const id = cleanText(ticketId);
  if (!id) return null;

  try {
    const { data: ticket, error } = await supabase
      .from("support_tickets")
      .select("id,ticket_number,requester_id,requester_role")
      .eq("id", id)
      .maybeSingle();

    if (error) throw error;
    const requesterId = cleanText(ticket?.requester_id);
    if (!requesterId) return null;

    const ticketReference = ticket?.ticket_number ? `#${ticket.ticket_number}` : "your support request";
    const message = cleanText(notification.message).replace("{ticket_reference}", ticketReference);

    return await createUserNotification({
      userId: requesterId,
      title: notification.title,
      message,
      type: notification.type,
      priority: "important",
      source: "system",
      audience: normalizeSupportRole(ticket?.requester_role) || "mixed",
      channels: { in_app: true, push: false, sound: false, vibration: false },
      quietByDefault: true,
      escalationEligible: true,
      groupKey: `support_ticket:${ticket.id}`,
    });
  } catch (error) {
    logSupportError("support requester notification failed", error, { ticketId: id });
    return null;
  }
}

async function notifyTicketOpened(ticket, { fromFeedback = false } = {}) {
  const requesterId = cleanText(ticket?.requester_id);
  if (!requesterId) return null;
  return notifySupportRequester(ticket?.id, {
    title: "Support ticket opened",
    message: fromFeedback
      ? "AfriMed™ Support opened support ticket {ticket_reference} from your feedback."
      : "AfriMed™ Support opened support ticket {ticket_reference} for your request.",
    type: `support_ticket_opened:${ticket?.id || "unknown"}`,
  });
}

export const supportService = {
  async createTicket(ticketData = {}) {
    try {
      const user = await getCurrentUser();
      if (!user) {
        return { data: null, error: { message: "Authentication required to create support ticket" } };
      }

      const subject = cleanText(ticketData?.subject);
      const description = cleanText(ticketData?.description);
      if (!subject || !description) {
        return { data: null, error: { message: "Subject and description are required." } };
      }

      const profile = await getRequesterProfile(user.id);
      const payload = buildTicketInsert(ticketData, user, profile);

      const { data, error } = await supabase
        .from("support_tickets")
        .insert(payload)
        .select(
          "id,ticket_number,subject,description,status,priority,type,category,requester_id,requester_email,requester_name,requester_role,source,source_route,source_reference,external_requester_name,external_requester_email,external_requester_phone,opened_by_admin_id,opened_from_channel,related_appointment_id,metadata_json,tags,created_at,updated_at,resolved_at",
        )
        .single();

      if (error) {
        logSupportError("createTicket failed", error, {
          userId: user.id,
          type: payload.type,
          category: payload.category,
          source: payload.source,
        });
      }

      return { data: normalizeTicket(data), error };
    } catch (error) {
      logSupportError("createTicket exception", error);
      return { data: null, error: { message: safeErrorMessage(error, "Failed to create support ticket") } };
    }
  },

  async getUserTickets(status = null) {
    try {
      const user = await getCurrentUser();
      if (!user) {
        return { data: [], error: { message: "Authentication required" } };
      }

      let query = supabase
        .from("support_tickets")
        .select(
          "id,ticket_number,subject,description,status,priority,type,category,requester_id,requester_email,requester_name,requester_role,source,source_route,source_reference,external_requester_name,external_requester_email,external_requester_phone,opened_by_admin_id,opened_from_channel,related_appointment_id,metadata_json,tags,created_at,updated_at,resolved_at",
        )
        .eq("requester_id", user.id)
        .order("created_at", { ascending: false });

      const normalizedStatus = normalizeStatus(status);
      if (normalizedStatus) {
        query = query.eq("status", normalizedStatus);
      }

      const { data, error } = await query;
      if (error) {
        logSupportError("getUserTickets failed", error, { userId: user.id, status: normalizedStatus || "all" });
      }

      return { data: Array.isArray(data) ? data.map(normalizeTicket) : [], error };
    } catch (error) {
      logSupportError("getUserTickets exception", error);
      return { data: [], error: { message: safeErrorMessage(error, "Failed to fetch support tickets") } };
    }
  },

  async getTicket(ticketId) {
    try {
      const user = await getCurrentUser();
      if (!user) {
        return { data: null, error: { message: "Authentication required" } };
      }

      const id = cleanText(ticketId);
      if (!id) return { data: null, error: { message: "Ticket id is required." } };

      const { data, error } = await supabase
        .from("support_tickets")
        .select(
          "id,ticket_number,subject,description,status,priority,type,category,requester_id,requester_email,requester_name,requester_role,source,source_route,source_reference,external_requester_name,external_requester_email,external_requester_phone,opened_by_admin_id,opened_from_channel,related_appointment_id,metadata_json,tags,created_at,updated_at,resolved_at,messages:support_ticket_messages(id,ticket_id,sender_id,sender_name,sender_email,message,is_internal,attachments,metadata_json,created_at,updated_at)",
        )
        .eq("id", id)
        .eq("requester_id", user.id)
        .single();

      if (error) {
        logSupportError("getTicket failed", error, { userId: user.id, ticketId: id });
      }

      return { data: normalizeTicket(data), error };
    } catch (error) {
      logSupportError("getTicket exception", error);
      return { data: null, error: { message: safeErrorMessage(error, "Failed to fetch support ticket") } };
    }
  },

  async addTicketMessage(ticketId, message) {
    try {
      const user = await getCurrentUser();
      if (!user) {
        return { data: null, error: { message: "Authentication required" } };
      }

      const id = cleanText(ticketId);
      const body = cleanText(message);
      if (!id || !body) {
        return { data: null, error: { message: "Ticket id and message are required." } };
      }

      const profile = await getRequesterProfile(user.id);
      const { data, error } = await supabase
        .from("support_ticket_messages")
        .insert({
          ticket_id: id,
          sender_id: user.id,
          sender_name: requesterName(profile, user),
          sender_email: cleanText(user.email),
          message: body,
          is_internal: false,
        })
        .select("id,ticket_id,sender_id,sender_name,sender_email,message,is_internal,attachments,metadata_json,created_at,updated_at")
        .single();

      if (error) {
        logSupportError("addTicketMessage failed", error, { userId: user.id, ticketId: id });
      }

      return { data, error };
    } catch (error) {
      logSupportError("addTicketMessage exception", error);
      return { data: null, error: { message: safeErrorMessage(error, "Failed to add support message") } };
    }
  },

  async getSupportStats() {
    try {
      const { data, error } = await this.getUserTickets();
      if (error) return { data: null, error };
      const tickets = Array.isArray(data) ? data : [];
      return {
        data: {
          total: tickets.length,
          open: tickets.filter((ticket) => ticket.status === "open").length,
          in_progress: tickets.filter((ticket) => ticket.status === "in_progress").length,
          resolved: tickets.filter((ticket) => ticket.status === "resolved").length,
          closed: tickets.filter((ticket) => ticket.status === "closed").length,
        },
        error: null,
      };
    } catch (error) {
      logSupportError("getSupportStats exception", error);
      return { data: null, error: { message: safeErrorMessage(error, "Failed to fetch support stats") } };
    }
  },

  getSupportOptions() {
    return {
      types: [
        { value: "technical_issue", label: "Technical Issue", description: "Problems with the platform or features" },
        { value: "billing_inquiry", label: "Billing Inquiry", description: "Questions about payments or billing" },
        { value: "account_access", label: "Account Access", description: "Login or account-related issues" },
        { value: "consultation_support", label: "Consultation Support", description: "Help with video consultations" },
        { value: "general_inquiry", label: "General Inquiry", description: "General questions about AfriMed™" },
        { value: "feature_request", label: "Feature Request", description: "Suggestions for new features" },
      ],
      priorities: [
        { value: "low", label: "Low", description: "Non-urgent inquiry or minor issue" },
        { value: "medium", label: "Medium", description: "Standard issue requiring attention" },
        { value: "high", label: "High", description: "Important issue affecting your use" },
        { value: "urgent", label: "Urgent", description: "Critical issue requiring immediate attention" },
      ],
    };
  },

  async listAdminTickets({ status = "", type = "", requesterRole = "", source = "", limit = 120 } = {}) {
    try {
      let query = supabase
        .from("support_tickets")
        .select(
          "id,ticket_number,subject,description,status,priority,type,category,requester_id,requester_email,requester_name,requester_role,source,source_route,source_reference,external_requester_name,external_requester_email,external_requester_phone,opened_by_admin_id,opened_from_channel,related_appointment_id,metadata_json,tags,created_at,updated_at,resolved_at",
        )
        .order("created_at", { ascending: false })
        .limit(Math.min(Math.max(Number(limit || 120), 1), 250));

      const normalizedStatus = normalizeStatus(status);
      if (normalizedStatus) query = query.eq("status", normalizedStatus);
      const normalizedType = normalizeSupportType(type);
      if (cleanText(type)) query = query.eq("type", normalizedType);
      const normalizedRole = normalizeSupportRole(requesterRole);
      if (normalizedRole) query = query.eq("requester_role", normalizedRole);
      const sourceValue = cleanText(source);
      if (sourceValue) query = query.eq("source", sourceValue);

      const { data, error } = await query;
      if (error) logSupportError("listAdminTickets failed", error);
      const normalized = Array.isArray(data) ? data.map(normalizeTicket) : [];
      return { data: await enrichRequesterIdentity(normalized), error };
    } catch (error) {
      logSupportError("listAdminTickets exception", error);
      return { data: [], error: { message: safeErrorMessage(error, "Failed to load support tickets") } };
    }
  },

  async getAdminTicket(ticketId) {
    try {
      const id = cleanText(ticketId);
      if (!id) return { data: null, error: { message: "Ticket id is required." } };
      const { data, error } = await supabase
        .from("support_tickets")
        .select(
          "id,ticket_number,subject,description,status,priority,type,category,requester_id,requester_email,requester_name,requester_role,source,source_route,source_reference,external_requester_name,external_requester_email,external_requester_phone,opened_by_admin_id,opened_from_channel,related_appointment_id,metadata_json,tags,created_at,updated_at,resolved_at,messages:support_ticket_messages(id,ticket_id,sender_id,sender_name,sender_email,message,is_internal,attachments,metadata_json,created_at,updated_at)",
        )
        .eq("id", id)
        .single();
      if (error) logSupportError("getAdminTicket failed", error, { ticketId: id });
      return { data: await enrichRequesterIdentity(normalizeTicket(data)), error };
    } catch (error) {
      logSupportError("getAdminTicket exception", error);
      return { data: null, error: { message: safeErrorMessage(error, "Failed to load support ticket") } };
    }
  },

  async searchAdminRequesters(search = "") {
    try {
      const term = cleanText(search);
      if (term.length < 2) return { data: [], error: null };
      const { data, error } = await supabase.rpc("list_admin_staging_user_support", {
        p_search: term,
        p_filter: "all",
        p_limit: 12,
      });
      if (error) {
        logSupportError("searchAdminRequesters failed", error);
        return { data: [], error };
      }
      const rows = Array.isArray(data) ? data : [];
      return {
        data: rows.map((row) => ({
          user_id: cleanText(row?.user_id),
          email: cleanText(row?.email),
          name: cleanText(row?.name) || cleanText(row?.email) || "AfriMed™ user",
          role: normalizeSupportRole(row?.role) || cleanText(row?.role) || "unknown",
          phone: cleanText(row?.phone),
          profile_record: row?.profile_record || null,
          doctor_profile_record: row?.doctor_profile_record || null,
          patient_profile_record: row?.patient_profile_record || null,
        })).filter((row) => row.user_id),
        error: null,
      };
    } catch (error) {
      logSupportError("searchAdminRequesters exception", error);
      return { data: [], error: { message: safeErrorMessage(error, "Failed to search users") } };
    }
  },

  async createAdminTicket(ticketData = {}) {
    try {
      const subject = cleanText(ticketData?.subject);
      const description = cleanText(ticketData?.description);
      if (!subject || !description) {
        return { data: null, error: { message: "Subject and description are required." } };
      }

      const requesterId = cleanText(ticketData?.requesterId) || null;
      const externalName = cleanText(ticketData?.externalRequesterName || ticketData?.requesterName);
      const externalEmail = cleanText(ticketData?.externalRequesterEmail || ticketData?.requesterEmail);
      const externalPhone = cleanText(ticketData?.externalRequesterPhone || ticketData?.requesterPhone);
      if (!requesterId && !externalName && !externalEmail && !externalPhone) {
        return { data: null, error: { message: "Select a requester or add external contact details." } };
      }

      const { data, error } = await supabase.rpc("create_support_ticket_admin", {
        p_requester_id: requesterId,
        p_requester_role: cleanText(ticketData?.requesterRole) || null,
        p_requester_name: cleanText(ticketData?.requesterName || externalName) || null,
        p_requester_email: cleanText(ticketData?.requesterEmail || externalEmail) || null,
        p_requester_phone: externalPhone || null,
        p_source: cleanText(ticketData?.source) || "admin_manual",
        p_source_reference: cleanText(ticketData?.sourceReference) || null,
        p_subject: subject,
        p_description: description,
        p_type: normalizeSupportType(ticketData?.type || ticketData?.category),
        p_priority: normalizePriority(ticketData?.priority),
        p_metadata: normalizeMetadata(ticketData?.metadata),
        p_internal_note: cleanText(ticketData?.internalNote) || null,
      });

      if (error) {
        logSupportError("createAdminTicket failed", error);
        return { data: null, error };
      }

      const enriched = await enrichRequesterIdentity(normalizeTicket(data));
      await notifyTicketOpened(enriched, { fromFeedback: cleanText(ticketData?.source) === "feedback" });
      return { data: enriched, error: null };
    } catch (error) {
      logSupportError("createAdminTicket exception", error);
      return { data: null, error: { message: safeErrorMessage(error, "Failed to open support ticket") } };
    }
  },

  async getSupportTicketForFeedback({ sourceTable = "", feedbackId = "" } = {}) {
    try {
      const source = cleanText(sourceTable);
      const id = cleanText(feedbackId);
      if (!source || !id) return { data: null, error: null };
      const { data, error } = await supabase
        .from("support_ticket_feedback_links")
        .select("ticket_id,feedback_source_table,feedback_id,support_tickets(id,ticket_number,status)")
        .eq("feedback_source_table", source)
        .eq("feedback_id", id)
        .maybeSingle();
      if (error) return { data: null, error };
      return { data, error: null };
    } catch (error) {
      logSupportError("getSupportTicketForFeedback exception", error);
      return { data: null, error: { message: safeErrorMessage(error, "Failed to check linked support ticket") } };
    }
  },

  async convertFeedbackToTicket({ sourceTable = "", feedbackId = "", type = "general_inquiry", priority = "medium", internalNote = "" } = {}) {
    try {
      const source = cleanText(sourceTable);
      const id = cleanText(feedbackId);
      if (!source || !id) return { data: null, error: { message: "Feedback id is required." } };

      const existing = await this.getSupportTicketForFeedback({ sourceTable: source, feedbackId: id });
      const alreadyLinked = Boolean(existing?.data?.ticket_id);

      const { data, error } = await supabase.rpc("convert_feedback_to_support_ticket", {
        p_feedback_source_table: source,
        p_feedback_id: id,
        p_type: normalizeSupportType(type),
        p_priority: normalizePriority(priority),
        p_internal_note: cleanText(internalNote) || null,
      });

      if (error) {
        logSupportError("convertFeedbackToTicket failed", error, { sourceTable: source, feedbackId: id });
        return { data: null, error };
      }

      const enriched = await enrichRequesterIdentity(normalizeTicket(data));
      if (!alreadyLinked) await notifyTicketOpened(enriched, { fromFeedback: true });
      return { data: enriched, error: null, alreadyLinked };
    } catch (error) {
      logSupportError("convertFeedbackToTicket exception", error);
      return { data: null, error: { message: safeErrorMessage(error, "Failed to convert feedback") } };
    }
  },

  async addAdminTicketMessage(ticketId, message, { isInternal = false, senderName = "", senderEmail = "" } = {}) {
    try {
      const user = await getCurrentUser();
      if (!user) return { data: null, error: { message: "Authentication required" } };
      const id = cleanText(ticketId);
      const body = cleanText(message);
      if (!id || !body) return { data: null, error: { message: "Ticket id and message are required." } };

      const profile = await getRequesterProfile(user.id);
      const { data, error } = await supabase
        .from("support_ticket_messages")
        .insert({
          ticket_id: id,
          sender_id: user.id,
          sender_name: cleanText(senderName) || requesterName(profile, user),
          sender_email: cleanText(senderEmail) || cleanText(user.email),
          message: body,
          is_internal: Boolean(isInternal),
        })
        .select("id,ticket_id,sender_id,sender_name,sender_email,message,is_internal,attachments,metadata_json,created_at,updated_at")
        .single();

      if (error) logSupportError("addAdminTicketMessage failed", error, { ticketId: id, isInternal: Boolean(isInternal) });
      if (!error && data && !isInternal) {
        await notifySupportRequester(id, {
          title: "New support reply",
          message: "AfriMed™ Support replied to your support request {ticket_reference}.",
          type: `support_ticket_reply:${id}:${data.id}`,
        });
      }
      return { data, error };
    } catch (error) {
      logSupportError("addAdminTicketMessage exception", error);
      return { data: null, error: { message: safeErrorMessage(error, "Failed to add support reply") } };
    }
  },

  async updateAdminTicket(ticketId, updates = {}) {
    try {
      const id = cleanText(ticketId);
      if (!id) return { data: null, error: { message: "Ticket id is required." } };
      const { data, error } = await supabase.rpc("update_support_ticket_admin", {
        ticket_id: id,
        next_status: normalizeStatus(updates.status) || null,
        next_priority: cleanText(updates.priority) ? normalizePriority(updates.priority) : null,
        next_assigned_to: cleanText(updates.assignedTo) || null,
        next_resolution: cleanText(updates.resolution) || null,
        next_internal_notes: cleanText(updates.internalNotes) || null,
        next_category: cleanText(updates.category) || null,
        next_source: cleanText(updates.source) || null,
      });
      if (error) logSupportError("updateAdminTicket failed", error, { ticketId: id });
      const normalized = normalizeTicket(data);
      const status = normalizeStatus(updates.status);
      if (!error && normalized && status) {
        const statusLabel = status.replaceAll("_", " ");
        await notifySupportRequester(id, {
          title: "Support request status updated",
          message: `Your support request ${normalized.ticket_number ? `#${normalized.ticket_number}` : ""} is now ${statusLabel}.`,
          type: `support_ticket_status:${id}:${status}`,
        });
      }
      return { data: await enrichRequesterIdentity(normalized), error };
    } catch (error) {
      logSupportError("updateAdminTicket exception", error);
      return { data: null, error: { message: safeErrorMessage(error, "Failed to update support ticket") } };
    }
  },

  subscribeToTicketUpdates(ticketId, callback) {
    return supabase
      .channel(`support_ticket_${ticketId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "support_ticket_messages",
          filter: `ticket_id=eq.${ticketId}`,
        },
        callback,
      )
      .subscribe();
  },

  unsubscribe(channel) {
    if (channel) {
      supabase.removeChannel(channel);
    }
  },

  async closeTicket() {
    return {
      data: null,
      error: { message: "Ticket closure is handled by AfriMed™ Support." },
    };
  },
};

export default supportService;
